import { forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Check, RotateCw, Gauge, Stamp } from "lucide-react";
import { TRANSLATIONS } from "@/i18n";
import {
  FADE_IN_INITIAL,
  FADE_IN_ANIMATE,
  FADE_OUT_EXIT,
  SLIDE_UP_INITIAL,
  SLIDE_UP_ANIMATE,
  SLIDE_UP_TRANSITION,
} from "@/constants/diplomate";

/**
 * BullshitScoreResultPanel — Score 0-100 (clamped server-side) + jargon + plain + short + diagnosis.
 * Le score est présenté comme « Diagnostic non homologué ISO 0000 ».
 */
export const BullshitScoreResultPanel = forwardRef(function BullshitScoreResultPanel(
  { lang, result, loading, copied, onCopy, onRegenerate },
  ref,
) {
  const t = TRANSLATIONS[lang];
  const hasResult = !!result;
  const score = hasResult ? Math.max(0, Math.min(100, Number(result.score) || 0)) : 0;
  const terms = hasResult ? result.detected_terms || [] : [];
  const barColor = score < 34 ? "#B5EAD7" : score < 67 ? "#FFF2B2" : "#F28E7C";
  const combinedForCopy = hasResult
    ? [
        `${t.bullshitScoreLabel} : ${score}%`,
        `\n${t.bullshitSections.terms}\n${terms.join(", ") || "-"}`,
        `\n${t.bullshitSections.plain}\n${result.plain_version || "-"}`,
        `\n${t.bullshitSections.short}\n${result.short_version || "-"}`,
        `\n${t.bullshitSections.diagnosis}\n${result.diagnosis || "-"}`,
      ].join("\n")
    : "";

  return (
    <div
      ref={ref}
      data-testid="bullshit-panel"
      className="border-2 border-black nb-shadow relative flex flex-col"
      style={{ background: hasResult ? "#FFF2B222" : "#FFFFFF" }}
    >
      <div className="flex items-center justify-between border-b-2 border-black px-5 py-3 bg-[#F3EFE7]">
        <div className="flex items-center gap-2">
          <Gauge className="w-3.5 h-3.5 text-black" />
          <span className="font-mono text-[11px] uppercase tracking-widest font-bold">
            {t.bullshitResultLabel}
          </span>
        </div>
        {hasResult && !loading && (
          <div className="flex items-center gap-2">
            <button
              data-testid="bullshit-regenerate-btn"
              onClick={onRegenerate}
              className="bg-white border-2 border-black nb-shadow-sm nb-hover px-2.5 py-1 font-bold text-[11px] uppercase tracking-widest flex items-center gap-1.5"
            >
              <RotateCw className="w-3 h-3" /> {t.again}
            </button>
            <button
              data-testid="bullshit-copy-btn"
              onClick={() => onCopy(combinedForCopy)}
              className="bg-white border-2 border-black nb-shadow-sm nb-hover px-2.5 py-1 font-bold text-[11px] uppercase tracking-widest flex items-center gap-1.5"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3" /> {t.copied}
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" /> {t.copy}
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <div className="min-h-[240px] sm:min-h-[300px] relative px-5 py-5">
        <AnimatePresence mode="wait">
          {loading && (
            <motion.div
              key="loading"
              initial={FADE_IN_INITIAL}
              animate={FADE_IN_ANIMATE}
              exit={FADE_OUT_EXIT}
              className="absolute inset-0 flex flex-col items-center justify-center gap-3"
            >
              <div className="flex gap-2">
                <span className="w-2.5 h-2.5 bg-black animate-pulse-dot" />
                <span className="w-2.5 h-2.5 bg-black animate-pulse-dot" style={{ animationDelay: "0.15s" }} />
                <span className="w-2.5 h-2.5 bg-black animate-pulse-dot" style={{ animationDelay: "0.3s" }} />
              </div>
              <p className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">
                {t.generating}
              </p>
            </motion.div>
          )}

          {!loading && hasResult && (
            <motion.div
              key="result"
              initial={SLIDE_UP_INITIAL}
              animate={SLIDE_UP_ANIMATE}
              transition={SLIDE_UP_TRANSITION}
              className="flex flex-col gap-5"
              data-testid="bullshit-result"
            >
              {/* Score panel */}
              <div className="bg-white border-2 border-black p-4 sm:p-5">
                <div className="flex items-baseline justify-between mb-2 gap-3">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">
                    {t.bullshitScoreLabel}
                  </span>
                  <span
                    className="font-display font-bold text-4xl sm:text-5xl tabular-nums"
                    data-testid="bullshit-score-value"
                  >
                    {score}
                    <span className="text-xl sm:text-2xl text-[#4A4A4A] ml-1">%</span>
                  </span>
                </div>
                <div className="w-full h-3 border-2 border-black bg-white overflow-hidden">
                  <div
                    className="h-full transition-all duration-500"
                    style={{ width: `${score}%`, background: barColor }}
                    aria-hidden
                  />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A] mt-2">
                  {t.bullshitScoreSub}
                </p>
              </div>

              {/* Terms */}
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="font-mono text-[10px] uppercase tracking-widest font-bold">
                    {t.bullshitSections.terms}
                  </span>
                  <span className="flex-1 h-px bg-black/15" />
                </div>
                {terms.length ? (
                  <div className="flex flex-wrap gap-1.5" data-testid="bullshit-terms">
                    {terms.map((term, i) => (
                      <span
                        key={`${term}-${i}`}
                        className="font-mono text-[11px] bg-white border border-black px-2 py-0.5"
                      >
                        {term}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm italic text-[#4A4A4A]">—</p>
                )}
              </div>

              {/* Plain */}
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="font-mono text-[10px] uppercase tracking-widest font-bold">
                    {t.bullshitSections.plain}
                  </span>
                  <span className="flex-1 h-px bg-black/15" />
                </div>
                <p className="text-base leading-[1.6] text-black whitespace-pre-wrap">
                  {result.plain_version}
                </p>
              </div>

              {/* Short */}
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="font-mono text-[10px] uppercase tracking-widest font-bold">
                    {t.bullshitSections.short}
                  </span>
                  <span className="flex-1 h-px bg-black/15" />
                </div>
                <p className="text-base leading-[1.6] text-black font-medium whitespace-pre-wrap bg-white border-2 border-black p-3">
                  {result.short_version}
                </p>
              </div>

              {/* Diagnosis stamp */}
              <div className="flex items-start gap-2 admin-stamp self-start px-2 py-1">
                <Stamp className="w-3 h-3 mt-0.5" strokeWidth={2.5} />
                <span className="text-[11px] italic">{result.diagnosis}</span>
              </div>
            </motion.div>
          )}

          {!loading && !hasResult && (
            <motion.div
              key="empty"
              initial={FADE_IN_INITIAL}
              animate={FADE_IN_ANIMATE}
              className="absolute inset-0 flex items-center justify-center text-center px-6"
            >
              <p className="text-[#4A4A4A] text-sm italic max-w-xs leading-relaxed">
                {t.bullshitOutputEmpty}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
});
